import { t } from '../../i18n/locale';

// Health-claims compliance UI (GDPR-006). Brand §7 of docs/brand/asmaluz/brand.md
// is BINDING: Asmaluz is a dietary supplement, NOT a drug — it does not diagnose,
// treat, cure, or prevent any disease. Every public benefit claim must carry the
// asterisk marker tying it to the FDA disclaimer, and the disclaimer must appear
// on every surface that shows a claim (home, catalog, product).
//
// This module is the single source of truth for the asterisk convention and the
// rendered disclaimer element. The disclaimer *text* is the i18n `common.fda`
// string (trilingual en/es/pt), so there is exactly one copy of the wording.

export const CLAIM_MARKER = '*';

// withClaimMarker ties a benefit claim to the FDA disclaimer with a single
// trailing asterisk. Idempotent: copy authored with the marker already (e.g. the
// i18n catalog claims) is returned unchanged, so it is safe to apply everywhere a
// claim is rendered rather than hand-typing a '*' per claim. Empty/blank input is
// returned untouched (no stray marker on a missing claim).
export function withClaimMarker(claim: string): string {
  const trimmed = claim.trimEnd();
  if (!trimmed) return trimmed;
  return trimmed.endsWith(CLAIM_MARKER) ? trimmed : `${trimmed}${CLAIM_MARKER}`;
}

// fdaDisclaimer builds the small-print FDA disclaimer rendered on every surface
// that shows benefit claims. The leading asterisk matches the claim markers. Pass
// { extra: true } to append the "consult your healthcare provider" caution
// (Brand §7) on surfaces that do not already carry the compliance footer.
export function fdaDisclaimer(opts: { extra?: boolean } = {}): HTMLParagraphElement {
  const p = document.createElement('p');
  p.className = 'disclaimer';
  const text = opts.extra ? `${t('common.fda')} ${t('compliance.extra')}` : t('common.fda');
  p.textContent = `${CLAIM_MARKER}${text}`;
  return p;
}
